import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Hourglass, FlaskConical, Download, ClipboardCheck, PlusCircle, Stethoscope } from "lucide-react";
import { downloadBlob } from "../components/FollowupFields";
import { CaseFilterBar, CaseIds } from "../components/CaseFilterBar";
import { ScanCaseButton } from "../components/ScanCaseDialog";
import { matchCase, caseKind } from "../lib/caseSearch";

const daysAgo = (iso) => Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);

export function PendingRow({ c }) {
  const exportLab = async () => {
    try {
      await downloadBlob(api, `/cases/${c.id}/lab-request-pdf`, `lab_request_${c.id}.pdf`);
    } catch {
      toast.error("No lab tests to request for this case");
    }
  };
  const d = daysAgo(c.created_at);
  return (
    <div data-testid={`pending-row-${c.id}`} className="flex items-center gap-4 px-5 py-4 flex-wrap sm:flex-nowrap">
      <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center shrink-0"><Hourglass className="w-5 h-5 text-amber-700" /></div>
      <div className="min-w-0 flex-1">
        <p className="font-medium text-slate-900 truncate">{c.patient?.name || "Unnamed patient"}</p>
        <CaseIds c={c} />
        <p className="text-sm text-slate-500 truncate">{c.procedure_name} · waiting {d === 0 ? "since today" : `${d} day${d > 1 ? "s" : ""}`}</p>
        <div className="flex flex-wrap gap-1.5 mt-1.5">
          {c.awaiting_clearance && (
            <span data-testid={`pending-awaiting-clearance-${c.id}`} className="inline-flex items-center gap-1 text-[11px] font-semibold bg-orange-50 text-orange-800 border border-orange-300 px-2 py-0.5 rounded-full">
              <Stethoscope className="w-3 h-3" /> Awaiting specialist approval: {(c.decision?.final_decision?.consult || []).map((x) => x.specialist).join(", ")}
            </span>
          )}
          {(c.decision?.followups || []).map((f) => (
            <span key={f.id} className="inline-flex items-center gap-1 text-[11px] font-medium bg-amber-50 text-amber-800 border border-amber-200 px-2 py-0.5 rounded-full">
              {c.pending_labs?.some((l) => l.id === f.id) && <FlaskConical className="w-3 h-3" />}{f.label}
            </span>
          ))}
        </div>
      </div>
      <div className="flex items-center gap-2">
        {c.pending_labs?.length > 0 && (
          <Button size="sm" variant="outline" onClick={exportLab} data-testid={`pending-lab-pdf-${c.id}`}><Download className="w-4 h-4 mr-1.5" /> Lab request</Button>
        )}
        <Link to={`/app/cases/${c.id}/results`}>
          <Button size="sm" data-testid={`pending-enter-${c.id}`} className={c.awaiting_clearance && !c.decision?.followups?.length ? "bg-orange-600 hover:bg-orange-700" : "bg-sky-700 hover:bg-sky-800"}>
            <ClipboardCheck className="w-4 h-4 mr-1.5" /> {c.awaiting_clearance && !c.decision?.followups?.length ? "Record approval" : "Enter results"}
          </Button>
        </Link>
      </div>
    </div>
  );
}

export default function PendingCases() {
  const [cases, setCases] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  useEffect(() => {
    api.get("/cases", { params: { status: "pending" } }).then(({ data }) => setCases(data)).catch(() => setCases([]));
  }, []);

  const shown = (cases || []).filter((c) => matchCase(c, q) && (filter === "all" || caseKind(c) === filter));

  return (
    <div data-testid="pending-cases-page">
      <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 flex items-center gap-2"><Hourglass className="w-7 h-7 text-amber-600" /> Pending Cases</h1>
      <p className="text-slate-500 mt-1">Cases waiting for lab results, missing information or specialist approval. Open a case when the patient returns to enter results and get the final recommendation.</p>
      <CaseFilterBar q={q} setQ={setQ} filter={filter} setFilter={setFilter}
        options={[["all", "All pending"], ["labs", "Awaiting results / info"], ["specialist", "Awaiting specialist"]]}
        counts={{ all: cases?.length, labs: cases?.filter((c) => caseKind(c) === "labs").length, specialist: cases?.filter((c) => caseKind(c) === "specialist").length }}>
        <ScanCaseButton />
      </CaseFilterBar>
      {cases === null ? null : cases.length === 0 ? (
        <div className="bg-white rounded-xl border border-dashed border-slate-300 p-12 text-center mt-6" data-testid="pending-empty">
          <ClipboardCheck className="w-9 h-9 text-slate-300 mx-auto" />
          <p className="text-slate-500 mt-3">No pending cases — everything is complete.</p>
          <Link to="/app/new"><Button className="mt-4 bg-slate-900 hover:bg-slate-800"><PlusCircle className="w-4 h-4 mr-2" /> New Evaluation</Button></Link>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 mt-6 divide-y divide-slate-100">
          {shown.length === 0 && <p className="p-8 text-center text-slate-500" data-testid="pending-no-match">No pending case matches your search.</p>}
          {shown.map((c) => <PendingRow key={c.id} c={c} />)}
        </div>
      )}
    </div>
  );
}
